// src/lib/sw-update/take-new-build.ts

import {useSwUpdateStore} from './store';

/**
 * Backstop for a handover that never lands.
 *
 * Posting SKIP_WAITING is not a promise of anything: the worker may have
 * activated between reading `waiting` and sending the message, in which case
 * `controllerchange` never fires and no reload follows. Without this the user
 * would sit on a disabled button forever, which is the stuck state this module
 * exists to remove. It is cleared the moment control does change, so the
 * graceful path keeps its precache even when the reload is slow to unload.
 */
export const CONTROL_HANDOVER_TIMEOUT_MS = 3000;

/** What the generated worker listens for to call `skipWaiting()` (see `dist/sw.js`). */
const SKIP_WAITING_MESSAGE = {type: 'SKIP_WAITING'} as const;

/**
 * The service worker registration for this origin, or null when service
 * workers are unavailable or blocked.
 */
async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    // navigator.serviceWorker throws in insecure/sandboxed contexts.
    return null;
  }
}

/**
 * Unregister the service workers matching `predicate`, returning how many were
 * dropped.
 *
 * Retiring the worker is what makes the *next* navigation reach nginx instead
 * of being answered from the old build's precache (`workbox.navigateFallback`
 * serves `index.html` from there). `main.tsx` re-registers on the following
 * load, which re-precaches the current build.
 */
export async function unregisterServiceWorkers(
  predicate: (registration: ServiceWorkerRegistration) => boolean = () => true,
): Promise<number> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 0;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const matching = registrations.filter(predicate);
    await Promise.all(matching.map((registration) => registration.unregister()));
    return matching.length;
  } catch {
    // navigator.serviceWorker throws in insecure/sandboxed contexts; callers
    // that follow up with a reload should still do so.
    return 0;
  }
}

/** Retire every worker, then reload — so the reload is answered by nginx. */
async function unregisterAndReload(): Promise<void> {
  await unregisterServiceWorkers();
  window.location.reload();
}

/**
 * Move this tab onto the current build. The single path for doing so: the
 * banner, the route-change hook and chunk recovery all call this, so there is
 * one implementation of the handoff and one in-flight guard behind it.
 *
 * Two paths, chosen on whether there is a worker to hand over to:
 *
 * - **Waiting worker.** Tell it to take control, and reload once it has. This
 *   keeps the precache, so the app stays installable and offline-capable.
 * - **Nothing waiting.** Unregister, then reload. A plain `location.reload()`
 *   would not be enough: the active (old) worker answers the navigation from
 *   its own precache and returns the same stale `index.html`. That is the
 *   DERP-266 mechanism, and it is why retiring the worker has to come first.
 *
 * The handover is driven from here rather than through `virtual:pwa-register`'s
 * `updateSW` helper. That helper only forwards SKIP_WAITING (its `reloadPage`
 * argument is discarded) and the reload arrives from a listener the plugin
 * registers out of sight — leaving nothing to cancel the backstop against.
 */
export async function takeNewBuild(): Promise<void> {
  const {isApplying, markApplying} = useSwUpdateStore.getState();
  if (isApplying) return;
  markApplying();

  const waiting = (await currentRegistration())?.waiting;
  if (!waiting) {
    await unregisterAndReload();
    return;
  }

  const backstop = window.setTimeout(() => void unregisterAndReload(), CONTROL_HANDOVER_TIMEOUT_MS);

  // Listen before asking, so a fast handover cannot land first.
  //
  // Do not remove this as redundant: `vite-plugin-pwa` attaches its own
  // `controlling` listener that reloads too (unconditionally, as a side effect
  // of `onNeedRefresh` firing — there is no opting out), so a successful
  // handover does reload twice. Harmless, but ours is the one holding
  // `backstop`, and without it the timer below fires on every handover and
  // unregisters the worker that has just activated.
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      window.clearTimeout(backstop);
      window.location.reload();
    },
    {once: true},
  );

  waiting.postMessage(SKIP_WAITING_MESSAGE);
}
